// apps/client/src/characters/hair.ts — the six hair styles as soft shells of locks over the head (characters pass, 2026-10-16). The town camera
// sees mostly the top of a head, so each style has its own silhouette from above and a hairline that leaves the face clear: grooves between
// locks run down from the crown (shaped and shaded, so they read at a distance), the fringe ends in rounded points, a soft sheen rings the
// crown, and a rolled lip gives the hair its thickness. Each style is one main mesh named `hair:<style>` (plus a bun, a drape or curls).
import * as THREE from 'three';
import { HEAD, onHead } from './head';
import { part, shade, type V3 } from './part';
import { blob, locks, shell, smooth, teardrop } from './shapes';

export const HAIR_STYLES = ['short', 'bob', 'bun', 'curly', 'long', 'buzz'] as const;
type Style = (typeof HAIR_STYLES)[number];

const Y = new THREE.Vector3(0, 1, 0), Z = new THREE.Vector3(0, 0, 1);
/** Front / side / back blend by azimuth (weights sum to 1): where the hair ends round the head. */
const around = (az: number, front: number, side: number, back: number) => {
  const c = Math.cos(az), s = Math.sin(az);
  return front * Math.max(0, c) ** 2 + side * s * s + back * Math.max(0, -c) ** 2;
};
/** 1 over the forehead, fading to 0 by the temples: where a fringe hangs. */
const fringe = (az: number) => 1 - smooth(0.55, 1.2, Math.abs(az));
const fine = (az: number) => (Math.abs(az) < 1.3 ? 0.075 : 0.2);
const radii = (t: number) => new THREE.Vector3(HEAD.r.x + t, HEAD.r.y + t, HEAD.r.z + t);
const centre: V3 = [HEAD.c.x, HEAD.c.y, HEAD.c.z];
/** Shading of a head of locks: grooves `depth` darker between `n` locks (fading out toward the crown) and a soft sheen ring. */
const lockTone = (n: number, phase: number, depth: number, sheen = 0.07) => (az: number, polar: number) =>
  1 - depth * (1 - locks(az, n, phase)) ** 1.5 * smooth(0.2, 0.75, polar) + sheen * Math.exp(-(((polar - 0.5) / 0.2) ** 2));

interface StyleDef {
  thick: number; rings: number; edge(az: number): number; puff?(az: number, polar: number): number; tone?(az: number, polar: number): number;
  step?(az: number): number; lip?: number;
}
const STYLES: Record<Style, StyleDef> = {
  // Side-swept locks over the forehead (longer toward the doll's right), the ears half covered, a tidy nape, a cowlick at the crown.
  short: {
    thick: 0.016, rings: 6,
    edge: (az) => around(az, 0.9, 1.55, 1.98) + fringe(az) * (0.16 + 0.07 * Math.tanh(-az * 2)) * locks(az, 15, -0.1) ** 1.3,
    puff: (az, polar) => 1 + 0.05 * Math.cos(polar * 0.9) - 0.04 * (1 - locks(az, 15, -0.1)) * smooth(0.3, 1.0, polar),
    tone: lockTone(15, -0.1, 0.2),
  },
  // A rounded helmet falling to the jaw and curving in under it, with a blunt fringe just above the eyes.
  bob: {
    thick: 0.018, rings: 7,
    edge: (az) => around(az, 0.98, 2.2, 2.2) + fringe(az) * 0.05 * locks(az, 34),
    puff: (az, polar) => 1 + 0.035 * Math.cos(polar) + 0.07 * smooth(1.0, 1.75, polar) * (1 - smooth(1.9, 2.25, polar) * 0.6) * (1 - Math.max(0, Math.cos(az)) ** 2)
      - 0.012 * (1 - locks(az, 16, 0.2)) * smooth(0.3, 0.9, polar),
    tone: lockTone(16, 0.2, 0.1),
  },
  // Sleek hair drawn back to a bun, parted on the doll's left; the tie wears the friend accent.
  bun: {
    thick: 0.013, rings: 5, step: (az) => (Math.abs(az) < 1.3 ? 0.1 : 0.22),
    edge: (az) => around(az, 0.98, 1.52, 1.9) + 0.06 * fringe(az) * Math.tanh(-az * 2) - 0.12 * Math.exp(-(((az - 0.5) / 0.16) ** 2)),
    puff: (az, polar) => 1 + 0.025 * Math.cos(polar) - 0.015 * (1 - locks(az, 20)) * smooth(0.3, 0.9, polar),
    tone: lockTone(20, 0, 0.16),
  },
  // A big soft cloud of curls with a bumpy, rounded hairline.
  curly: {
    thick: 0.03, rings: 6, step: () => 0.15,
    edge: (az) => around(az, 0.98, 1.72, 2.0) + 0.1 * locks(az, 20) ** 0.6 * (0.6 + 0.4 * fringe(az)),
    puff: (az, polar) => 1 + 0.06 * Math.cos(polar * 0.8) + 0.04 * Math.max(0, Math.cos(az * 7 + polar * 3)) * Math.max(0, Math.cos(polar * 8)),
    tone: (az, polar) => 0.9 + 0.12 * Math.max(0, Math.cos(az * 7 + polar * 3)) * Math.max(0, Math.cos(polar * 8)),
  },
  // Long hair: side-swept fringe, curtains past the jaw and a drape down the back.
  long: {
    thick: 0.017, rings: 6,
    edge: (az) => around(az, 0.94, 2.1, 2.35) + fringe(az) * (0.12 + 0.07 * Math.tanh(az * 2)) * locks(az, 15, 0.1) ** 1.3,
    puff: (az, polar) => 1 + 0.04 * Math.cos(polar) + 0.05 * smooth(1.3, 2.2, polar) * (1 - Math.max(0, Math.cos(az)) ** 2) - 0.03 * (1 - locks(az, 18)) * smooth(0.4, 1.2, polar),
    tone: lockTone(18, 0, 0.18),
  },
  // Close-cropped: a thin cap following the head.
  buzz: {
    thick: 0.005, rings: 5, step: (az) => (Math.abs(az) < 1.3 ? 0.14 : 0.26), lip: 0.03,
    edge: (az) => around(az, 0.82, 1.45, 1.88) + 0.03 * fringe(az) * Math.cos(az * 6),
    tone: (_az, polar) => 1 + 0.06 * Math.exp(-(((polar - 0.5) / 0.25) ** 2)),
  },
};
/** A child part of the main hair mesh at a point on the head (hair parts hang off `main`, which sits at the head's centre). */
const onMain = (lat: number, lon: number, lift: number): { at: V3; n: THREE.Vector3 } => {
  const { p, n } = onHead(lat, lon, lift);
  return { at: [p.x - HEAD.c.x, p.y - HEAD.c.y, p.z - HEAD.c.z], n };
};

/** A resident's hair in `hex`; unknown style ids fall back to 'short'. `accent`: the bun's tie. `skin` tints a buzz cut. */
export function addHair(head: THREE.Object3D, hex: string, style: string | undefined, accent: string, skin: string): THREE.Mesh {
  const id: Style = (HAIR_STYLES as readonly string[]).includes(style ?? '') ? (style as Style) : 'short';
  const d = STYLES[id];
  const r = radii(d.thick);
  const geo = shell({ radii: r, edge: d.edge, rings: d.rings, step: d.step ?? fine, ...(d.puff ? { puff: d.puff } : {}), ...(d.tone ? { tone: d.tone } : {}),
    lip: d.lip ?? (d.thick + 0.004) / r.y });
  const colour = id === 'buzz' ? `#${new THREE.Color(hex).lerp(new THREE.Color(skin), 0.22).getHexString()}` : hex;
  const main = part(head, geo, colour, centre, `hair:${id}`);
  if (id === 'short') {
    for (const [lon, len, lean] of [[2.6, 0.07, 0.5], [2.9, 0.06, 0.8]] as const) {    // a cowlick at the crown breaks the dome from above
      const { at, n } = onMain(1.1, lon, d.thick - 0.006);
      const tuft = part(main, teardrop(len, 0.024, 6, 4), hex, at);
      tuft.quaternion.setFromUnitVectors(Y, n.clone().add(new THREE.Vector3(0, 0, -lean)).normalize());
      tuft.scale.z = 0.6;
    }
  } else if (id === 'bun') {
    const { at, n } = onMain(1.0, Math.PI, 0.04);
    part(main, blob(0.064, 0.058, 0.06, 10, 8), hex, at).quaternion.setFromUnitVectors(Z, n);
    const tie = onMain(1.0, Math.PI, 0.002);
    part(main, new THREE.TorusGeometry(0.036, 0.011, 4, 12), accent, tie.at).quaternion.setFromUnitVectors(Z, tie.n);
  } else if (id === 'long') {
    // The drape: a soft flattened mass falling from the back of the head to below the shoulders.
    const drape = part(main, blob(0.15, 0.16, 0.08, 10, 7), shade(hex, 0.96), [0, -0.12, -0.085]);
    drape.rotation.x = 0.12;
    for (const sx of [-1, 1]) {                                        // two locks over the shoulders frame the face
      const lock = part(main, teardrop(0.15, 0.034, 6, 5), shade(hex, 0.97), [sx * 0.135, -0.02, 0.02]);
      lock.rotation.set(0.12, 0, Math.PI + sx * 0.12);
      lock.scale.z = 0.7;
    }
  } else if (id === 'curly') {
    // Curls scattered over the crown (a golden-angle spiral), so the cloud reads from above.
    for (let i = 0; i < 10; i++) {
      const lat = 1.45 - 0.66 * Math.sqrt((i + 0.5) / 10), lon = i * 2.4;
      const { at, n } = onMain(lat, lon, d.thick - 0.004);
      part(main, blob(0.032, 0.032, 0.022, 8, 6), shade(hex, i % 2 ? 1.06 : 0.95), at).quaternion.setFromUnitVectors(Z, n);   // half sunk: soft bumps
    }
  }
  return main;
}
